<template>
    <div class="credit-note">
        <v-alert
            v-if="creditNote.client"
            class="ma-4"
            border="left"
            prominent
            color="warning"
            type="info"
            text
        >
            <strong>{{ $tc('general.buyer', 1) }}: {{ creditNote.client.name }}</strong>
            <br>
            {{ !isSummary ? $t('order.credit_note_message') : $t('order.credit_note_message_summary') }}
        </v-alert>

        <v-data-table
            ref="table"
            class="items-table"
            :headers="headers"
            :items="items"
            :items-per-page="-1"
            hide-default-footer
            :mobile-breakpoint="0"
            dense
        >
            <template v-slot:item.selected="{ item }">
                <v-checkbox
                    v-show="item.updated"
                    :value="!!item.updated"
                    color="error"
                    dense
                    hide-details
                    @change="unselectItem(item)"
                />
            </template>

            <template v-slot:item.orderNumber="{ item }">
                <div :class="{ updated: item.updated }">
                    {{ item.orderNumber }}
                </div>
            </template>

            <template v-slot:item.code="{ item }">
                <div class="code" :class="{ updated: item.updated }">
                    {{ item.product ? item.product.code : '' }}
                </div>
            </template>

            <template v-slot:item.name="{ item }">
                <div class="name" :class="{ updated: item.updated }">
                    {{ item.name }}
                </div>
            </template>

            <template v-slot:item.count="{ item }">
                <div :class="{ updated: item.updated }">
                    {{ item.getCount() }}
                </div>
            </template>

            <template v-slot:item.unitPriceBeforeTax="{ item }">
                <div :class="{ updated: item.updated }">
                    {{ item.getUnitPriceBeforeTax().toFormat(isSummary ? '0.00' : '0.0000') }}
                </div>
            </template>

            <template v-slot:item.taxRate="{ item }">
                <div :class="{ updated: item.updated }">
                    {{ item.getTaxRate() }}%
                </div>
            </template>

            <template v-slot:item.discountPercent="{ item }">
                <div :class="{ updated: item.updated }">
                    {{ item.getDiscountPercent() }}%
                </div>
            </template>

            <template v-slot:item.tax="{ item }">
                <div :class="{ updated: item.updated }">
                    {{ item.getTax().toFormat('0.00') }}
                </div>
            </template>

            <template v-slot:item.priceBeforeTax="{ item }">
                <div :class="{ updated: item.updated }">
                    {{ item.getPriceBeforeTax().toFormat('0.00') }}
                </div>
            </template>

            <template v-slot:item.additionalCount="{ item }">
                <div v-if="item.updated" class="wfull updated">
                    {{ item.getAdditionalCount() }}
                </div>
                <v-edit-dialog
                    v-if="!item.updated"
                    large
                    :cancel-text="$t('general.cancel')"
                    :save-text="$t('general.save')"
                    @open="openEditDialog(item, 'count')"
                    @save="editItem(item, 'count')"
                >
                    <div class="wfull">
                        <v-icon size="16" class="mr-1">fal fa-pen</v-icon>
                        {{ item.getAdditionalCount() }}
                    </div>
                    <template v-slot:input>
                        <div class="mt-4 text-h6">
                            {{ $t('general.return_count') }}
                        </div>
                        <number-field
                            ref="count"
                            v-model="count"
                            :label="$t('general.quantity')"
                            :precision="3"
                            single-line
                            unsigned
                        />
                    </template>
                </v-edit-dialog>
            </template>

            <template v-slot:item.additionalDiscountPercent="{ item }">
                <div v-if="item.updated" class="wfull updated">
                    <span v-if="item.getAdditionalDiscountPercent() > 0">-</span>{{ item.getAdditionalDiscountPercent() }}%
                </div>
                <v-edit-dialog
                    v-if="!item.updated"
                    large
                    :cancel-text="$t('general.cancel')"
                    :save-text="$t('general.save')"
                    @open="openEditDialog(item, 'discountPercent')"
                    @save="editItem(item, 'discountPercent')"
                >
                    <div class="wfull">
                        <v-icon size="16" class="mr-1">fal fa-pen</v-icon>
                        {{ item.getAdditionalDiscountPercent() }}%
                    </div>
                    <template v-slot:input>
                        <div class="mt-4 text-h6">
                            {{ $t('general.additional_discount') }}
                        </div>
                        <number-field
                            ref="discountPercent"
                            v-model="discountPercent"
                            :label="$t('general.discount')"
                            :precision="2"
                            single-line
                            unsigned
                        />
                    </template>
                </v-edit-dialog>
            </template>

            <template v-slot:item.priceBeforeTaxAdditional="{ item }">
                <div v-if="item.updated" class="updated">
                    {{ item.getAdditionalPriceBeforeTax().toFormat('0.00') }}
                </div>
                <v-edit-dialog
                    v-if="!item.updated"
                    large
                    :cancel-text="$t('general.cancel')"
                    :save-text="$t('general.save')"
                    @open="openEditDialog(item, 'discountAmount')"
                    @save="editItem(item, 'discountAmount')"
                >
                    <div class="wfull">
                        <v-icon size="16" class="mr-1">fal fa-pen</v-icon>
                        {{ item.getAdditionalPriceBeforeTax().toFormat('0.00') }}
                    </div>
                    <template v-slot:input>
                        <div class="mt-4 text-h6">
                            {{ $t('general.amount') }}
                        </div>
                        <number-field
                            ref="discountAmount"
                            v-model="discountAmount"
                            :label="$t('general.discount')"
                            :precision="2"
                            single-line
                            unsigned
                        />
                    </template>
                </v-edit-dialog>
            </template>
        </v-data-table>

        <v-row class="summary flex-wrap-reverse">
            <v-col cols="12" sm="8" class="info-container">
                <v-row>
                    <v-col cols="12" sm="6">
                        <v-text-field
                            v-model="creditNote.internalNumberSuffix"
                            :prefix="creditNoteNumberPrefix"
                            :label="$t('general.document_number')"
                        />
                    </v-col>
                    <v-col cols="12" sm="6">
                        <v-select
                            v-model="creditNote.paymentType"
                            :items="paymentTypes"
                            item-value="value"
                            item-text="display"
                            :label="$t('general.paid_by')"
                            clear-icon="fal fa-times"
                        />
                    </v-col>
                </v-row>
                <v-textarea
                    v-model="creditNote.note"
                    class="mt-2"
                    :label="$t('general.note')"
                    rows="3"
                />
            </v-col>
            <v-col cols="12" sm="4" class="taxes-container">
                <template v-for="(tax, index) in order.taxes">
                    <div
                        v-if="!tax.id && tax.price_before_tax"
                        :key="index"
                        class="summary-item"
                    >
                        <div>{{ $t('order.exempt_from_tax') }} {{ tax.label.replace('VAT_','') }}:</div>
                        <div class="amount">{{ Money.parse(tax.price_before_tax).toFormat('0.00') }}</div>
                    </div>

                    <div
                        v-if="!tax.id"
                        :key="'additional-discount-' + index"
                        class="summary-item"
                    >
                        <div>{{ $t('general.additional_discount') }}:</div>
                        <div class="amount">{{ Money.parse(tax.additional_discount_amount).toFormat('0.00') }}</div>
                    </div>

                    <div
                        v-if="!tax.id"
                        :key="'additional-count-' + index"
                        class="summary-item"
                    >
                        <div>{{ $t('general.return_amount') }}:</div>
                        <div class="amount">{{ Money.parse(tax.additional_count_amount).toFormat('0.00') }}</div>
                    </div>
                </template>

                <div v-if="order.taxes.some(tax => !tax.id)" class="item-divider"></div>

                <template v-for="(tax, index) in order.taxes">
                    <div
                        v-if="tax.id && tax.price_before_tax"
                        :key="'beforetax-' + index"
                        class="summary-item"
                    >
                        <div>
                            <span v-if="tax.name">{{ tax.name }} - </span>
                            <span>{{ `${tax.label} ${$t('general.price_pre_tax').toLowerCase()}:` }}</span>
                        </div>
                        <div class="amount">{{ Money.parse(tax.price_before_tax).toFormat('0.00') }}</div>
                    </div>

                    <div
                        v-if="tax.id"
                        :key="index"
                        class="summary-item"
                    >
                        <div>
                            <span v-if="tax.name">{{ tax.name }} - </span>
                            <span>{{ `${tax.label} (${tax.rate}%):` }}</span>
                        </div>
                        <div class="amount">{{ Money.parse(tax.value).toFormat('0.00') }}</div>
                    </div>

                    <div
                        v-if="tax.id"
                        :key="'additional-discount-' + index"
                        class="summary-item"
                    >
                        <div>{{ $t('general.additional_discount') }}:</div>
                        <div class="amount">{{ Money.parse(tax.additional_discount_amount).toFormat('0.00') }}</div>
                    </div>

                    <div
                        v-if="tax.id"
                        :key="'additional-count-' + index"
                        class="summary-item"
                    >
                        <div>{{ $t('general.return_amount') }}:</div>
                        <div class="amount">{{ Money.parse(tax.additional_count_amount).toFormat('0.00') }}</div>
                    </div>

                    <div v-if="tax.id" class="item-divider"></div>
                </template>

                <div v-if="order.getExemptFromTax().toUnit() > 0" class="summary-item">
                    <div>{{ $t('invoice_preview.summary.exempt_from_tax') }}:</div>
                    <div class="amount">{{ order.getExemptFromTax().toFormat('0.00') }}</div>
                </div>

                <div class="summary-item">
                    <div>{{ $t('invoice_preview.summary.price_before_tax') }}:</div>
                    <div class="amount">{{ order.getPriceBeforeTax().toFormat('0.00') }}</div>
                </div>

                <div class="summary-item">
                    <div>{{ $t('invoice_preview.summary.tax') }}:</div>
                    <div class="amount">{{ order.getTax().toFormat('0.00') }}</div>
                </div>

                <div class="item-divider"></div>

                <div class="summary-item">
                    <div>{{ $t('invoice_preview.summary.discount') }}:</div>
                    <div class="amount">{{ order.getAdditionalDiscountAmount().toFormat('0.00') }}</div>
                </div>

                <div class="summary-item">
                    <div>{{ $t('general.return_amount') }}:</div>
                    <div class="amount">{{ order.getAdditionalCountAmount().toFormat('0.00') }}</div>
                </div>

                <div class="item-divider"></div>

                <div class="summary-item to-pay">
                    <div>{{ $t('general.total') }}:</div>
                    <div class="amount">{{ order.getToPay().toFormat('0.00') }}</div>
                </div>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import NumberField from '@/components/NumberField';
import OrderService from '@/services/OrderService';

export default {
    components: {
        NumberField
    },

    props: {
        orders: {
            type: Array,
            default: () => []
        },
        summary: {
            type: Boolean,
            default: false
        },
    },

    created() {
        this.items = this.getItems()
        this.originalItems = this.items.map(i => i.$duplicate())

        this.creditNote.internalNumber = OrderService.getNextInternalNumber()
        this.creditNote.internalNumberSuffix = null;
        this.creditNote.paymentType = 'invoice';
        this.creditNote.client = this.orders.length ? this.orders[0].client : null;
        this.creditNote.note = this.tenant.venue.invoice_note;
    },

    data: () => ({
        discount: null,
        returnAmount: null,
        items: [],
        updatedItems: [],

        count: null,
        discountPercent: null,
        discountAmount: null,

        creditNote: {
            internalNumber: null,
            internalNumberSuffix: null,
            paymentType: 'invoice',
            client: null,
            note: null,
        },
    }),

    watch: {
        items(val) {
            this.updatedItems = val.filter(item => !!item?.updated).map(item => {
                const itemCopy = item.$duplicate()
                delete itemCopy.orderNumber
                delete itemCopy.product
                delete itemCopy.updated
                return itemCopy
            })

            this.$emit('enable-issue', this.updatedItems.length > 0);
        }
    },

    computed: {
        headers() {
            const headers = [{ text: '', value: 'selected', sortable: false, class: 'pa-0' }];

            if(!this.isSummary) {
                headers.push({ text: this.$t('general.receipt_number'), value: 'orderNumber' });
                headers.push({ text: this.$t('general.code'), value: 'code' });
                headers.push({ text: this.$tc('general.product', 1), value: 'name' });
                headers.push({ text: this.$t('general.quantity'), value: 'count' });
                headers.push({ text: this.$t('general.unit_price_before_tax'), value: 'unitPriceBeforeTax' });
                headers.push({ text: this.$t('general.tax_rate'), value: 'taxRate' });
                headers.push({ text: this.$t('general.discount'), value: 'discountPercent' });
                headers.push({ text: this.$t('general.price_pre_tax_total'), value: 'priceBeforeTax' });
                headers.push({ text: this.$t('general.additional_discount'), value: 'additionalDiscountPercent', class: 'separator', cellClass: 'separator' });
                headers.push({ text: this.$t('general.return_count'), value: 'additionalCount' });
                headers.push({ text: this.$t('general.credit_note_amount'), value: 'priceBeforeTaxAdditional' });
            } else {
                headers.push({ text: this.$t('tax_rates.rate'), value: 'name' });
                headers.push({ text: this.$t('general.unit_price_before_tax'), value: 'unitPriceBeforeTax' });
                headers.push({ text: this.$t('general.tax_rate'), value: 'taxRate' });
                headers.push({ text: this.$t('general.tax_amount'), value: 'tax' });
                headers.push({ text: this.$t('general.additional_discount'), value: 'additionalDiscountPercent', class: 'separator', cellClass: 'separator' });
                headers.push({ text: this.$t('general.credit_note_amount'), value: 'priceBeforeTaxAdditional' });
            }

            return headers
        },

        paymentTypes() {
            const paymentTypes = ['invoice']

            if(locale === 'me') {
                paymentTypes.push('credit_card_business')
            }

            return paymentTypes.map(type => {
                return {
                    value: type,
                    display: this.$tc(`orders.payment_types.${type}`, 2),
                }
            })
        },

        order() {
            const curShift = Shift.current();

            const order = new Order({
                shift_id: curShift ? curShift.id : null,
                shift_hash: curShift ? curShift.hash : null,
                items: this.updatedItems,
                status: Order.STATUS_CREDIT_NOTE,
            })

            order.taxes = Order.calculateTaxes(order);

            return order
        },

        creditNoteNumberPrefix() {
            return this.creditNote.internalNumber + (this.creditNote.internalNumberSuffix ? '/' : '')
        },

        isSummary() {
            return this.summary || this.orders[0].isEinvoice();
        }
    },

    methods: {
        getItems() {
            if(this.isSummary) {
                const taxItems = TaxItem.merge(this.orders.flatMap(o => o.taxes))
                    .filter(taxItem => taxItem.id !== null && taxItem.rate > 0);
                const orderItems = taxItems.map(taxItem => OrderItem.createForTaxItem(this.orders[0], taxItem))
                return orderItems.map(item => {
                    item.orderNumber = '';
                    item.product = null;
                    item.updated = null;
                    return item
                });
            }

            return this.orders.flatMap(order => {
                return order.$duplicate().items
                    .filter(item => !item.exempt_from_tax)
                    .map(item => {
                        item.orderNumber = order.number;
                        item.product = item.getProduct();
                        item.updated = null;
                        return item
                    })
            })
        },

        createCreditNote() {
            if(!this.updatedItems.length) return

            const updatedOrderHashes = this.updatedItems.map(item => item.order_hash)
            const updatedOrders = !this.isSummary
                ? this.orders.filter(order => updatedOrderHashes.includes(order.hash))
                : this.orders
            const updatedOrdersUnique = updatedOrders.filter((order, index, array) => index === array.indexOf(order));

            const internalNumber = this.creditNoteNumberPrefix + (this.creditNote.internalNumberSuffix || '')

            this.$emit('create', {
                orders: updatedOrdersUnique,
                items: this.updatedItems,
                options: {
                    paymentType: this.creditNote.paymentType,
                    client: this.creditNote.client,
                    internalNumber: internalNumber,
                    note: this.creditNote.note,
                    flag: this.isSummary ? 'type,summaryCreditNote' : null,
                    choosePrinter: true,
                }
            })
        },

        openEditDialog(item, field) {
            if(field === 'count') {
                this.count = -1 * item.getAdditionalCount();
            } else if(field === 'discountPercent') {
                this.discountPercent = item.getAdditionalDiscountPercent();
            } else if(field === 'discountAmount') {
                this.discountAmount = item.getAdditionalPriceBeforeTax().toRoundedUnit(2);
            }

            this.$nextTick(() => {
                if(this.$refs[field] && this.$refs[field].$refs.textField) {
                    this.$refs[field].$refs.textField.$refs.input.setSelectionRange(0, 10000)
                }
            })
        },

        editItem(item, field) {
            if(field === 'count') {
                const count = _.round(-1 * this.count, 3)
                const resultingCount = item.getCount() + count
                if(count == 0 || resultingCount < 0) {
                    return
                }
                item.updateAdditionalCount(count)
            } else if(field === 'discountPercent') {
                item.giveAdditionalDiscount(this.discountPercent)
                this.discountAmount = item.getAdditionalPriceBeforeTax();
                if(item.getAdditionalDiscountPercent() == 0) {
                    return
                }
            } else if(field === 'discountAmount') {
                this.discountPercent = Money.parse(this.discountAmount * 100).divide(item.getPriceBeforeTax().toUnit()).toRoundedUnit(2)
                item.giveAdditionalDiscount(this.discountPercent)
                this.discountAmount = item.getAdditionalPriceBeforeTax().toUnit();
                if(item.getAdditionalDiscountPercent() == 0) {
                    return
                }
            }

            item.updated = field

            const index = this.items.findIndex(i => i.hash === item.hash)
            this.$set(this.items, index, item)
        },

        unselectItem(item) {
            const originalItem = this.originalItems.find(i => i.hash === item.hash)
            const itemIndex = this.items.findIndex(i => i.hash === item.hash)

            this.$set(this.items, itemIndex, originalItem.$duplicate())
        },
    }
};
</script>

<style lang="scss" scoped>
.credit-note {
    .internal-number {
        max-width: 300px;
    }

    ::v-deep .items-table {
        th {
            padding: 5px;
            vertical-align: middle;
            background: #f2f2f2;
        }

        td {
            font-size: 13px !important;
            padding: 5px;

            &:first-child {
                padding: 0;
                width: 42px;
                text-align: right !important;

                .v-input {
                    width: 30px;
                    margin-left: 12px;
                    margin-top: 0px;
                    padding: 0px;

                    .v-icon {
                        color: var(--v-error-base);
                    }
                }
            }

            .code {
                max-width: 60px;
            }

            .name {
                min-width: 120px;
            }

            .updated {
                color: var(--v-error-base);
            }

            .v-small-dialog__activator__content {
                width: 100%;
            }
        }

        .separator {
            border-left: 1px solid rgba(0, 0, 0, 0.5);
            padding-left: 12px;
        }
    }

    .summary {
        font-size: 14px;
        margin-top: 12px;
        display: flex;
        justify-content: space-between;
        padding: 12px;

        .item-divider {
            border-bottom: 1px solid black;
            margin: 5px 0;
        }

        .info-container {
            flex: 1;
            padding: 12px;
        }

        .taxes-container {
            padding: 12px;
        }

        .summary-item {
            padding: 3px;
            display: flex;
            justify-content: space-between;

            &.total {
                font-weight: 600;
                font-size: 16px;
            }

            &.to-pay {
                font-weight: 600;
                font-size: 16px;
            }

            .amount {
                text-align: right;
            }
        }
    }
}
</style>
